import { effect, inject, Injector, type EffectRef } from '@angular/core';
import { Router, type CanActivateFn, type UrlTree } from '@angular/router';
import { AccountService } from './account';

/**
 * Redirects to the dashboard unless `hasRight` holds; waits for the account so a
 * cold deep link isn't bounced before it loads, nor after a failed load that the
 * error screen's retry recovers.
 */
export function accountRightGuard(
  hasRight: (account: AccountService) => boolean
): CanActivateFn {
  return () => {
    const account = inject(AccountService);
    const router = inject(Router);
    const injector = inject(Injector);

    const decide = (): boolean | UrlTree =>
      hasRight(account) || router.createUrlTree(['/dashboard']);

    if (account.account() != null) {
      return decide();
    }

    // A self-destroying effect: `toObservable` would leak one per navigation.
    return new Promise<boolean | UrlTree>((resolve) => {
      let answered = false;
      const watcher: EffectRef = effect(
        () => {
          if (answered || account.account() == null) return;
          answered = true;
          resolve(decide());
          watcher.destroy();
        },
        { injector }
      );
    });
  };
}
